/**
 * IMPORTS
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { PlusCircle } from "lucide-react";

// components
import { Button } from "@components/button/button";
import { SelectPagination } from "@components/select-pagination/select-pagination";
import { Spinner } from "@components/spinner/spinner";
import { DynamicTable } from "@components/table-custom/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/tabs";

import { CreateClientModal } from "@pages/admin/gerenciar-clientes/components/create-client-modal/create-cliente-modal";
import { ExcludeItem } from "@pages/admin/gerenciar-clientes/components/exclude-item";
// components pages
import { AddCreditToClienteModal } from "@pages/admin/gerenciar-clientes/components/modal-adicionar-creditos-para-cliente";
import { SearchInputClient } from "@pages/admin/gerenciar-clientes/components/search-client";
import { abrirEdicaoCliente } from "@pages/admin/gerenciar-clientes/helpers/abrir-edicao-cliente";
import { getGerenciarClientesTableActions } from "@pages/admin/gerenciar-clientes/helpers/actions-table";
import { dataTitleTableCustomers } from "@pages/admin/gerenciar-clientes/helpers/data";

// hooks
import { useManageClient } from "@/hooks/usuários/buscar-clientes/use-manage-client";

// store-zustand
import { useStoreZustandManageClient } from "@/store-zustand/manage-client/manege-client";
import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

// typings
import type { ICliente } from "@/store-zustand/manage-client/index.d";

import { FEATURES, hasFeature } from "@/utils/auth/features";

const ManageClient = () => {
  const { features } = useStoreZustandUserAuth();
  const podeCriarCliente = hasFeature(features, FEATURES.CRIAR_CLIENTE);
  const podeEditarCliente = hasFeature(features, FEATURES.EDITAR_CLIENTE);

  const router = useNavigate();
  const {
    currentPage,
    setCurrentPage,
    totalItemsPage,
    itemsPerPage,
    setItemsPerPage,
    setIsModalCreateClient,
    isModalCreateClient,
    lastPage,
    isLoading: isLoadingMainCliente,
    userIdCliente,
    setUserIdCliente,
    setClienteEditar,
    isModalAddCredit,
    setIsModalAddCredit,
    isModalExclude,
    setIsModalExclude,
  } = useStoreZustandManageClient();

  const { clientes, paginations, isLoading } = useManageClient();

  /**função para navegar entre as páginas */
  const handleOnGoToPage = useCallback(
    (action: number | "prev" | "next") => {
      const newPage =
        action === "prev"
          ? Math.max(1, currentPage - 1)
          : action === "next"
            ? currentPage + 1
            : action;

      setCurrentPage(newPage);
    },
    [currentPage, setCurrentPage]
  );

  // Função para lidar com a alteração do seletor de itens por página
  const handleItemsPerPageChange = useCallback(
    async (event: React.ChangeEvent<HTMLSelectElement>) => {
      setItemsPerPage(Number(event.target.value));
    },
    [setItemsPerPage]
  );

  return (
    <>
      {isLoadingMainCliente ? (
        <Spinner />
      ) : (
        <>
          <Tabs defaultValue="all">
            <div className="mb-4 flex items-center">
              <TabsList className="flex">
                <TabsTrigger value="all">Ativos</TabsTrigger>
              </TabsList>
              <div className="ml-auto flex items-center gap-2">
                <div className="hidden md:block">
                  {/* Adicionando o seletor de itens por página */}
                  <SelectPagination
                    itemsPerPage={itemsPerPage}
                    handleItemsPerPageChange={handleItemsPerPageChange}
                  />
                </div>

                {podeCriarCliente && (
                  <Button
                    size="sm"
                    className="h-8 gap-1"
                    onClick={() => setIsModalCreateClient(true)}
                  >
                    <PlusCircle className="h-3.5 w-3.5" />
                    <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">
                      Adiconar cliente
                    </span>
                  </Button>
                )}
              </div>
            </div>

            {/* Conteúdo da aba "Clientes ativos*/}
            <TabsContent value="all">
              <DynamicTable
                title="Clientes"
                description="Gerencie seus clientes e visualize quando quiser."
                isLoadingPage={isLoading}
                columns={dataTitleTableCustomers}
                data={clientes}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                totalItems={totalItemsPage}
                lastPage={lastPage}
                onGoToPage={handleOnGoToPage}
                paginations={paginations}
                onRowClick={
                  podeEditarCliente
                    ? (cliente: ICliente) =>
                        abrirEdicaoCliente({ router, setClienteEditar, cliente })
                    : undefined
                }
                actions={getGerenciarClientesTableActions({
                  router,
                  features,
                  setUserIdCliente,
                  setClienteEditar,
                  isModalAddCredit,
                  setIsModalAddCredit,
                  isModalExclude,
                  setIsModalExclude,
                })}
                childerSearch={<SearchInputClient />}
              />
            </TabsContent>

            {/* Conteúdo da aba "Clientes inativos*/}
            <TabsContent value="inativos">
              <h1 className="text-red-700">INATIVOS</h1>
            </TabsContent>

            {/* Conteúdo da aba "Clientes inadiplentes*/}
            <TabsContent value="inadiplentes">
              <h1 className="text-red-700">INADIPLENTES</h1>
            </TabsContent>
          </Tabs>

          {/**MODAIS */}

          {podeCriarCliente && isModalCreateClient && (
            <CreateClientModal
              handleToggleCreateActvityModal={() => {
                setIsModalCreateClient(false);
              }}
            />
          )}

          {isModalAddCredit && userIdCliente && (
            <AddCreditToClienteModal
              userIdCliente={userIdCliente}
              handleToggleCreateActvityModal={() => setIsModalAddCredit(false)}
            />
          )}

          {isModalExclude && (
            <ExcludeItem isOpen={isModalExclude} onCancel={() => setIsModalExclude(false)} />
          )}
        </>
      )}
    </>
  );
};

/**
 * EXPORTS
 */
export { ManageClient };
